import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Target, CheckCircle2, AlertTriangle, Lightbulb, ShieldCheck, Mail, Briefcase } from "lucide-react";
import { CvShell } from "@/components/cv/CvShell";
import { CvGate } from "@/components/cv/CvGate";
import { useAuth } from "@/context/AuthContext";
import { useCredits } from "@/context/CreditsContext";
import { cvApi } from "@/lib/api";

const scoreColor = (s) => (s >= 75 ? "#16a34a" : s >= 50 ? "#ca8a04" : "#dc2626");

const List = ({ items, icon: Icon, color, testid }) => (
  <ul className="space-y-1.5" data-testid={testid}>
    {items.map((t, i) => (
      <li key={i} className="flex items-start gap-2 text-[13.5px] text-brand-text">
        <Icon size={15} className="mt-0.5 shrink-0" style={{ color }} /> <span>{t}</span>
      </li>
    ))}
  </ul>
);

export default function CvMatch() {
  const { user } = useAuth();
  const { confirmAndRun, priceOf } = useCredits();
  const navigate = useNavigate();
  const [cvs, setCvs] = useState([]);
  const [cvId, setCvId] = useState("");
  const [offer, setOffer] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [creatingLetter, setCreatingLetter] = useState(false);
  const [savingApp, setSavingApp] = useState(false);

  useEffect(() => {
    if (!user) return;
    cvApi.list().then((d) => { setCvs(d.items || []); if (d.items?.[0]) setCvId(d.items[0].id); }).catch(() => {});
  }, [user]);

  const loadHistory = (id) => {
    cvApi.matchHistory(id).then((d) => setHistory(d.items || [])).catch(() => setHistory([]));
  };
  useEffect(() => { if (user && cvId) loadHistory(cvId); }, [user, cvId]);

  const openHistory = async (id) => {
    try {
      const d = await cvApi.matchHistoryItem(id);
      setResult(d.result);
      if (d.offer_excerpt) setOffer(d.offer_excerpt);
    } catch {
      toast.error("Nie udało się wczytać analizy.");
    }
  };

  const analyze = async () => {
    if (!cvId) { toast.error("Wybierz CV do analizy."); return; }
    if (!offer.trim()) { toast.error("Wklej treść oferty pracy."); return; }
    setResult(null);
    const res = await confirmAndRun("cv_job_match", async () => {
      setLoading(true);
      try {
        return await cvApi.matchOffer({ cv_id: cvId, job_offer: offer });
      } finally {
        setLoading(false);
      }
    });
    if (res && res.result) {
      setResult(res.result);
      loadHistory(cvId);
    }
  };

  const createLetterFromOffer = async () => {
    if (!cvId) { toast.error("Wybierz CV."); return; }
    if (!offer.trim()) { toast.error("Wklej treść oferty."); return; }
    setCreatingLetter(true);
    try {
      const highlights = (result?.mocne_strony || []).slice(0, 6).join(", ");
      const l = await cvApi.letterCreate({ cv_id: cvId, job_offer: offer, highlights, company: result?.firma || "", role: result?.stanowisko || "" });
      navigate(`/cv/list/${l.id}`);
    } catch {
      toast.error("Nie udało się utworzyć listu.");
      setCreatingLetter(false);
    }
  };

  const saveAsApplication = async () => {
    if (!cvId) { toast.error("Wybierz CV."); return; }
    setSavingApp(true);
    try {
      await cvApi.appCreate({ cv_id: cvId, company: result?.firma || "", role: result?.stanowisko || "", job_offer: offer });
      toast.success("Zapisano jako aplikację. Znajdziesz ją w „Moje aplikacje”.");
    } catch {
      toast.error("Nie udało się zapisać aplikacji.");
    } finally {
      setSavingApp(false);
    }
  };

  const inputCls = "w-full rounded-button border border-brand-line bg-brand-bg px-3 py-2.5 text-sm text-brand-text focus:border-brand-accent focus:outline-none";

  return (
    <CvShell active="dopasowanie">
      <div data-testid="cv-match" className="pb-24">
        <h1 className="font-display text-2xl font-semibold text-brand-text sm:text-3xl">Dopasowanie CV do oferty</h1>
        <p className="mt-2 max-w-2xl text-brand-sec">Wklej treść ogłoszenia, a my porównamy je z Twoim CV i podpowiemy, co warto podkreślić. Wynik jest doradczy — nie zmieniamy Twojego CV.</p>

        {!user ? (
          <div className="mt-8 max-w-2xl"><CvGate returnTo="/cv/dopasowanie" message="Załóż bezpłatne konto, aby analizować dopasowanie do ofert." /></div>
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            {/* Wejście */}
            <div className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-brand-text">Twoje CV</span>
                <select data-testid="cv-match-cv-select" className={inputCls} value={cvId} onChange={(e) => setCvId(e.target.value)}>
                  {cvs.length === 0 && <option value="">Brak zapisanych CV</option>}
                  {cvs.map((c) => (<option key={c.id} value={c.id}>{c.title}</option>))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-brand-text">Treść oferty pracy</span>
                <textarea data-testid="cv-match-offer" rows={14} className={inputCls} value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="Wklej pełną treść ogłoszenia: obowiązki, wymagania, mile widziane…" />
              </label>
              <button type="button" data-testid="cv-match-analyze" disabled={loading} onClick={analyze} className="inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-50">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Target size={16} />} Analizuj dopasowanie{priceOf("cv_job_match") ? ` · ${priceOf("cv_job_match")} kr` : ""}
              </button>
              <p className="flex items-start gap-2 text-[12px] text-brand-muted"><ShieldCheck size={14} className="mt-0.5 shrink-0 text-brand-accent" /> Podpowiedzi opierają się na porównaniu oferty z Twoim CV. Dodawaj tylko informacje zgodne z rzeczywistością.</p>

              {history.length > 0 && (
                <div className="rounded-panel border border-brand-line bg-brand-surface p-4" data-testid="cv-match-history">
                  <p className="mb-2 text-[13px] font-semibold text-brand-text">Poprzednie analizy ({history.length})</p>
                  <ul className="space-y-2">
                    {history.map((h) => (
                      <li key={h.id}>
                        <button type="button" data-testid={`cv-match-history-item-${h.id}`} onClick={() => openHistory(h.id)} className="flex w-full items-center gap-3 rounded-card border border-brand-line bg-brand-bg p-2.5 text-left hover:border-brand-accent/60">
                          {typeof h.dopasowanie === "number" && (
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-white" style={{ background: scoreColor(h.dopasowanie) }}>{h.dopasowanie}%</span>
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] text-brand-text">{h.offer_excerpt || "Analiza"}</span>
                            <span className="block text-[11.5px] text-brand-muted">{new Date(h.created_at).toLocaleString("pl-PL")}</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Wynik */}
            <div>
              {loading ? (
                <div className="flex h-full min-h-[300px] items-center justify-center rounded-panel border border-brand-line bg-brand-surface"><Loader2 className="animate-spin text-brand-accent" /></div>
              ) : !result ? (
                <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-3 rounded-panel border border-dashed border-brand-line px-6 text-center text-brand-muted" data-testid="cv-match-empty">
                  <Target size={30} className="text-brand-line" />
                  <p>Wynik analizy pojawi się tutaj.</p>
                </div>
              ) : (
                <div className="space-y-5 rounded-panel border border-brand-line bg-brand-surface p-5" data-testid="cv-match-result">
                  {typeof result.dopasowanie === "number" && (
                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-lg font-bold text-white" style={{ background: scoreColor(result.dopasowanie) }} data-testid="cv-match-score">
                        {result.dopasowanie}%
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-brand-text">Szacowane dopasowanie</p>
                        {result.podsumowanie && <p className="text-[13px] text-brand-sec">{result.podsumowanie}</p>}
                      </div>
                    </div>
                  )}
                  {result.mocne_strony?.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-[13px] font-semibold text-brand-text">Mocne strony</p>
                      <List items={result.mocne_strony} icon={CheckCircle2} color="#16a34a" testid="cv-match-strengths" />
                    </div>
                  )}
                  {result.braki?.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-[13px] font-semibold text-brand-text">Braki względem oferty</p>
                      <List items={result.braki} icon={AlertTriangle} color="#ca8a04" testid="cv-match-gaps" />
                    </div>
                  )}
                  {result.podpowiedzi?.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-[13px] font-semibold text-brand-text">Co warto dodać lub podkreślić</p>
                      <List items={result.podpowiedzi} icon={Lightbulb} color="#2563eb" testid="cv-match-tips" />
                    </div>
                  )}
                  <div className="border-t border-brand-line pt-4">
                    <button type="button" data-testid="cv-match-create-letter" disabled={creatingLetter} onClick={createLetterFromOffer} className="inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-50">
                      {creatingLetter ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Utwórz list motywacyjny z tej oferty
                    </button>
                    <button type="button" data-testid="cv-match-save-app" disabled={savingApp} onClick={saveAsApplication} className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-button border border-brand-line px-5 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60 disabled:opacity-50">
                      {savingApp ? <Loader2 size={16} className="animate-spin" /> : <Briefcase size={16} />} Zapisz jako aplikację
                    </button>
                    <p className="mt-1.5 text-[12px] text-brand-muted">Przeniesiemy ofertę i Twoje atuty do kreatora listu — wystarczy uzupełnić firmę i stanowisko.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </CvShell>
  );
}
